// Reject a non-Maps paste before it reaches tina. The worker only recognises
// Google Maps hosts (short links it expands, plus maps.google.<tld> /
// google.<tld>/maps full URLs); anything else yields no cid/coords and the
// event is silently bound to city-level coords with a junk `mapsUrl`. Mirror
// tina's host recognition (src/shared/places.py _SHORT_LINK_HOSTS + the
// venue_resolution maps-url parsers) so a bad paste is caught at submit time.
const SHORT_LINK_HOSTS = ["maps.app.goo.gl", "goo.gl"];
// google.<tld> with at most a two-level suffix (.com, .de, .co.uk) — anchored
// so "google.com.evil.com" / "notgoogle.com" don't slip through.
const GOOGLE_HOST = /^(?:www\.)?google\.[a-z]{2,}(?:\.[a-z]{2,})?$/;
const MAPS_GOOGLE_HOST = /^maps\.google\.[a-z]{2,}(?:\.[a-z]{2,})?$/;

/** True when the pasted value is a Google Maps link tina can resolve. */
export function isGoogleMapsUrl(raw: string | null | undefined): boolean {
  const text = (raw ?? "").trim();
  if (!text) return false;
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return false;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  const host = url.hostname.toLowerCase();
  if (SHORT_LINK_HOSTS.some((h) => host === h || host.endsWith(`.${h}`)))
    return true;
  if (MAPS_GOOGLE_HOST.test(host)) return true;
  if (GOOGLE_HOST.test(host) && url.pathname.startsWith("/maps")) return true;
  return false;
}

/**
 * A Google Maps link centered on a plain lat/lng, for the "use this city's
 * center" autofill button. It carries no `!3d!4d` place segment, so tina's
 * `_coords_from_maps_url` reads no coordinates out of the link itself — the
 * autofill always pairs it with an explicit `geodata_id`, and
 * `resolve_location_from_maps_link` falls back to that geodata's own
 * `geolocation` (its intentional coordinate-less-URL behavior). This link is
 * still a real, correct, clickable Google Maps URL — only its own text isn't
 * what tina reads for coordinates.
 */
export function cityMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/@${lat},${lng},13z`;
}

function toCoord(value: unknown): number | null {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * The venue coordinates behind a geodata miss: the chosen Places candidate
 * carries `lat`/`lng` (`places_candidates[0]`), with the untrimmed
 * `selected_candidate_full` as a fallback. Returns null when neither has
 * usable coordinates, so the caller can skip the "closest cities" lookup.
 */
export function readCandidateCoords(
  problem: Record<string, unknown>,
): { lat: number; lng: number } | null {
  const candidates = Array.isArray(problem.places_candidates)
    ? problem.places_candidates
    : [];
  const sources = [candidates[0], problem.selected_candidate_full];
  for (const source of sources) {
    const c = (source ?? {}) as Record<string, unknown>;
    const lat = toCoord(c.lat);
    const lng = toCoord(c.lng);
    if (lat !== null && lng !== null) return { lat, lng };
  }
  return null;
}
